/**
 * PDF de trazabilidad de una operación SNT.
 *
 * Layout (paquete V refactor):
 *  - Header naranja con título + meta-info
 *  - Bloque de datos en grilla de 2 columnas (label | valor) con altura
 *    calculada dinámicamente para texto multi-línea
 *  - Bloque de mensajes ANMAT (los últimos 3 — request/response al SNT)
 *  - Tabla de series con paginación automática (4 columnas, ~50 series por
 *    página)
 *  - Footer con paginación + timestamp
 *
 * Anteriormente el helper usaba un `LINE=5` fijo que no consideraba la altura
 * real del texto multi-línea — causaba superposición cuando un valor era
 * largo (ej. evento ANMAT con descripción de 70+ chars). Ahora calculamos
 * cada salto con `getTextDimensions` o `splitTextToSize().length * lineHeight`.
 */

import { fmtDateTime, fmtRemitoNumero } from '@/lib/format';
import type { Operacion } from '@/api/contracts/operaciones';
import type { Serie } from '@/api/contracts/series';
import type { Producto } from '@/api/contracts/productos';
import type { Ubicacion } from '@/api/contracts/ubicaciones';
import type { Usuario } from '@/domain/types';
import type { EventoAnmat } from '@/api/contracts/eventos-anmat';

interface GenerarPdfTrazabilidadInput {
  operacion: Operacion;
  series: Serie[];
  productos: readonly Producto[];
  ubicaciones: readonly Ubicacion[];
  usuario: Usuario | null;
  eventoAnmat: EventoAnmat | null;
}

const TIPO_LABEL: Record<Operacion['tipo'], string> = {
  despacho_transferencia: 'Despacho · Transferencia',
  despacho_venta: 'Despacho · Venta',
  despacho_destruccion: 'Despacho · Destrucción',
  recepcion: 'Recepción',
};

// Dimensiones A4 + márgenes
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const BOTTOM = PAGE_H - 18;
// Altura efectiva de cada línea de texto. Para fontSize 9 con factor 1.4 da
// ~5mm. Usamos 5.5mm fijo para tener aire visual entre líneas.
const LINE = 5.5;

export async function generarPdfTrazabilidad({
  operacion,
  series,
  productos,
  ubicaciones,
  usuario,
  eventoAnmat,
}: GenerarPdfTrazabilidadInput): Promise<void> {
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });

  // ---- Cursor vertical (mutable). Cada bloque ajusta `y`. ----
  let y = MARGIN;

  // Helper local: dibuja texto con wrap automático y devuelve la altura
  // consumida (en mm), basada en cantidad real de líneas.
  const drawTextBlock = (
    text: string,
    x: number,
    yStart: number,
    maxWidth: number,
  ): number => {
    // splitTextToSize devuelve array de líneas que caben en maxWidth
    const lineas = pdf.splitTextToSize(text, maxWidth) as string[];
    pdf.text(lineas, x, yStart);
    return lineas.length * LINE;
  };

  // ============================================================
  // HEADER
  // ============================================================
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(30, 40, 70);
  pdf.text('EVEREX · Comprobante de trazabilidad SNT', MARGIN, y);
  y += 7;

  // Línea naranja Everex
  pdf.setLineWidth(0.5);
  pdf.setDrawColor(230, 126, 34);
  pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 7;

  // Subtítulo: tipo de operación (izq) + ID (der). En la MISMA línea.
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(30, 40, 70);
  pdf.text(TIPO_LABEL[operacion.tipo], MARGIN, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(120, 120, 120);
  pdf.text(`Op: ${operacion.id}`, PAGE_W - MARGIN, y, { align: 'right' });
  y += 8;

  // ============================================================
  // BLOQUE DE DATOS
  // ============================================================
  pdf.setTextColor(40, 40, 40);
  pdf.setFontSize(9);

  const origen = ubicaciones.find((u) => u.id === operacion.ubicacionId);
  const destino = operacion.destinoUbicacionId
    ? ubicaciones.find((u) => u.id === operacion.destinoUbicacionId)
    : null;

  const datos: Array<[string, string]> = [
    ['Fecha', fmtDateTime(operacion.fecha)],
    ['Remito', operacion.remitoNumero ? fmtRemitoNumero(operacion.remitoNumero) : '—'],
    ['Origen', origen ? `${origen.nombre} (GLN ${origen.gln})` : '—'],
    ['Destino', destino ? `${destino.nombre} (GLN ${destino.gln})` : '—'],
    ['Usuario', usuario ? `${usuario.nombre} (${usuario.username})` : '—'],
    ['Estado ANMAT', operacion.anmat.estado.toUpperCase()],
    ['Código transacción', operacion.anmat.codigoTransaccion ?? '—'],
    ['Tipo mensaje', operacion.anmat.tipoMensaje],
    [
      'Evento ANMAT',
      eventoAnmat ? `#${eventoAnmat.id} · ${eventoAnmat.evento}` : '—',
    ],
    ['Series total', String(operacion.seriesIds.length)],
  ];

  // Ancho del label fijo + width disponible para el valor (con padding)
  const LABEL_W = 38;
  const VALUE_X = MARGIN + LABEL_W;
  const VALUE_W = PAGE_W - MARGIN * 2 - LABEL_W;

  for (const [label, valor] of datos) {
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(80, 80, 80);
    pdf.text(`${label}:`, MARGIN, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 30, 30);
    // drawTextBlock devuelve la altura real consumida (puede ser multi-línea)
    const altura = drawTextBlock(valor, VALUE_X, y, VALUE_W);
    // Avanzamos por el máximo entre la altura del valor y un mínimo de 1 línea
    y += Math.max(altura, LINE);
  }

  // ---- Fila adicional si hubo cancelación ----
  if (operacion.fechaCancelacion) {
    y += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(180, 30, 30);
    pdf.text('Cancelada:', MARGIN, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(180, 30, 30);
    pdf.text(fmtDateTime(operacion.fechaCancelacion), VALUE_X, y);
    y += LINE;
  }

  // Separador
  y += 3;
  pdf.setLineWidth(0.2);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 5;

  // ============================================================
  // MENSAJES ANMAT (últimos 3 — request/response)
  // ============================================================
  if (operacion.anmat.mensajes.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(30, 40, 70);
    pdf.text('Mensajes ANMAT', MARGIN, y);
    y += 6;

    pdf.setFontSize(8);
    pdf.setFont('courier', 'normal');
    const recientes = operacion.anmat.mensajes.slice(-3);
    for (const m of recientes) {
      if (y > BOTTOM - 20) {
        pdf.addPage();
        y = MARGIN;
      }
      pdf.setTextColor(80, 80, 80);
      pdf.text(
        `[${fmtDateTime(m.ts)}] ${m.tipo.toUpperCase()}`,
        MARGIN,
        y,
      );
      y += LINE - 1;
      pdf.setTextColor(30, 30, 30);
      const altura = drawTextBlock(
        m.body,
        MARGIN + 3,
        y,
        PAGE_W - MARGIN * 2 - 3,
      );
      y += altura + 1;
    }

    y += 2;
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 5;
  }

  // ============================================================
  // LISTADO DE SERIES — tabla con columnas
  // ============================================================
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(30, 40, 70);
  pdf.text(`Series (${operacion.seriesIds.length})`, MARGIN, y);
  y += 6;

  // 4 columnas: SN | Lote | Producto | (vacío para padding)
  const SERIES_PER_ROW = 2; // 2 columnas a 1/2 de ancho para evitar truncado
  const COL_W = (PAGE_W - MARGIN * 2) / SERIES_PER_ROW;
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(40, 40, 40);

  for (let i = 0; i < operacion.seriesIds.length; i++) {
    const col = i % SERIES_PER_ROW;
    // Si volvemos a col=0 (inicio de nueva fila), bajar y
    if (col === 0 && i > 0) {
      y += LINE - 0.5;
    }
    // Page break si la próxima fila no entra
    if (y > BOTTOM) {
      pdf.addPage();
      y = MARGIN;
      pdf.setFont('courier', 'normal');
      pdf.setFontSize(7.5);
    }

    const sid = operacion.seriesIds[i];
    if (!sid) continue;
    const s = series.find((srs) => srs.id === sid);
    const prod = s ? productos.find((p) => p.gtin === s.gtin) : null;
    const productoNombre = prod ? prod.nombre.slice(0, 22) : '';
    const lote = s ? s.lote : '';
    const linea = `${sid}  ${lote ? '· ' + lote : ''}  ${productoNombre ? '· ' + productoNombre : ''}`.trim();
    const x = MARGIN + col * COL_W;
    pdf.text(linea, x, y, { maxWidth: COL_W - 2 });
  }

  // ============================================================
  // FOOTER (en todas las páginas)
  // ============================================================
  const lastPage = pdf.getNumberOfPages();
  for (let p = 1; p <= lastPage; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Riga Traza · ${fmtDateTime(new Date().toISOString())} · Página ${p}/${lastPage}`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: 'center' },
    );
  }

  pdf.save(`trazabilidad-${operacion.id}.pdf`);
}
